import React, { useEffect, useMemo, useState } from "react";
import {
  FiActivity,
  FiBriefcase,
  FiChevronDown,
  FiChevronUp,
  FiCreditCard,
  FiFileText,
  FiFolder,
  FiHash,
  FiLayers,
  FiMail,
  FiMap,
  FiMapPin,
  FiRotateCcw,
  FiSearch,
  FiSliders,
  FiUser,
} from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import {
  VISIBLE_ADVANCED_CASE_TABS,
  ADVANCED_CLIENT_TYPES,
  ADVANCED_MIN_LENGTHS,
  ADVANCED_STATUSES,
  BRAZILIAN_STATES,
  COCO_NAME_PARAM,
  SEARCH_ROLES,
  emptyAdvancedCriteria,
  getAdvancedFields,
  hasActiveCriteria,
  maskDocumentInput,
  maskPostalCodeInput,
  validateAdvancedCriteria,
} from "../helpers/advancedSearchHelper";
import CoCounselSelect from "./CoCounselSelect";

const FIELD_CONFIG = {
  document: { Icon: FiCreditCard, inputMode: "numeric", mask: maskDocumentInput },
  name: { Icon: FiUser },
  masterSchedule: { Icon: FiLayers },
  matterNumber: { Icon: FiFileText },
  email: { Icon: FiMail, type: "email", inputMode: "email" },
  cocoId: { Icon: null },
  city: { Icon: FiMapPin },
  state: { Icon: FiMap, options: ["", ...BRAZILIAN_STATES] },
  postalCode: { Icon: FiHash, inputMode: "numeric", mask: maskPostalCodeInput },
  caseTab: { Icon: FiFolder, options: VISIBLE_ADVANCED_CASE_TABS, optionKey: "caseTabs" },
  status: { Icon: FiActivity, options: ADVANCED_STATUSES, optionKey: "statuses" },
  clientType: { Icon: FiBriefcase, options: ADVANCED_CLIENT_TYPES, optionKey: "clientTypes" },
};

const AdvancedSearchPanel = ({
  role = SEARCH_ROLES.COORDINATOR,
  criteria,
  onSearch,
  onClear,
  loading = false,
  idToken,
  footer = null,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => criteria || emptyAdvancedCriteria());
  const [errors, setErrors] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  const fields = getAdvancedFields(role);
  // UF and CEP are short: they share one grid cell.
  const cells = fields.reduce((acc, field) => {
    if (field === "postalCode" && acc[acc.length - 1] === "state") acc[acc.length - 1] = ["state", "postalCode"];
    else acc.push(field);
    return acc;
  }, []);
  const criteriaKey = JSON.stringify(criteria || {});

  // URL is the source of truth: re-sync when it changes (chips, back/forward).
  useEffect(() => {
    setDraft(criteria || emptyAdvancedCriteria());
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteriaKey]);

  const canSearch = useMemo(() => hasActiveCriteria(draft, role), [draft, role]);

  const setField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const submit = () => {
    if (!canSearch || loading) return;
    const nextErrors = validateAdvancedCriteria(draft, role);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onSearch(draft);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submit();
  };

  // Native selects don't submit on Enter; make every field behave the same.
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && event.target.tagName === "SELECT") {
      event.preventDefault();
      submit();
    }
  };

  const handleClear = () => {
    setDraft(emptyAdvancedCriteria());
    setErrors({});
    onClear();
  };

  const renderControl = (field) => {
    const config = FIELD_CONFIG[field];
    const inputId = `adv-search-${field}`;
    const errorId = `${inputId}-error`;
    const error = errors[field];
    const describedBy = error ? errorId : undefined;

    if (field === "cocoId") {
      return (
        <CoCounselSelect
          id={inputId}
          idToken={idToken}
          value={{ cocoId: draft.cocoId, cocoName: draft[COCO_NAME_PARAM] }}
          onChange={({ cocoId, cocoName }) => setDraft((current) => ({
            ...current,
            cocoId,
            [COCO_NAME_PARAM]: cocoName,
          }))}
          onSubmitRequest={submit}
        />
      );
    }

    const { Icon } = config;
    if (config.options) {
      return (
        <div className="adv-search-input-wrap">
          <Icon className="adv-search-input-icon" aria-hidden="true" />
          <select
            id={inputId}
            className="portal-input adv-search-input adv-search-select"
            value={draft[field]}
            aria-describedby={describedBy}
            onChange={(event) => setField(field, event.target.value)}
          >
            {config.options.map((option) => (
              <option key={option || "any"} value={option}>
                {config.optionKey
                  ? t(`advancedSearchForm.${config.optionKey}.${option}`)
                  : option || t("advancedSearchForm.stateAny")}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div className="adv-search-input-wrap">
        <Icon className="adv-search-input-icon" aria-hidden="true" />
        <input
          id={inputId}
          type={config.type || "text"}
          inputMode={config.inputMode}
          autoComplete="off"
          className={`portal-input adv-search-input${error ? " invalid" : ""}`}
          placeholder={t(`advancedSearchForm.placeholders.${field}`)}
          value={draft[field]}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          onChange={(event) => setField(field, config.mask ? config.mask(event.target.value) : event.target.value)}
        />
      </div>
    );
  };

  const renderField = (field) => (
    <div key={field} className={`adv-search-field adv-search-field--${field}`}>
      <label className="portal-label" htmlFor={`adv-search-${field}`}>
        {t(`advancedSearchForm.fields.${field}`)}
      </label>
      {renderControl(field)}
      {errors[field] && (
        <p id={`adv-search-${field}-error`} className="adv-search-field-error">
          {t(`advancedSearchForm.validation.${errors[field]}`).replace("{min}", ADVANCED_MIN_LENGTHS[field])}
        </p>
      )}
    </div>
  );

  return (
    <section className="portal-page-panel adv-search-panel" aria-labelledby="adv-search-title">
      <div className="adv-search-header">
        <span className="adv-search-header-icon" aria-hidden="true"><FiSliders /></span>
        <div className="adv-search-heading">
          <h3 id="adv-search-title">{t("advancedSearchForm.title")}</h3>
          <p>{t("advancedSearchForm.subtitle")}</p>
        </div>
        <div className="adv-search-actions">
          <button type="button" className="adv-search-clear" onClick={handleClear}>
            <FiRotateCcw aria-hidden="true" />
            {t("advancedSearchForm.clearAll")}
          </button>
          <button
            type="submit"
            form="adv-search-form"
            className="portal-button adv-search-submit"
            disabled={!canSearch || loading}
            aria-describedby={canSearch ? undefined : "adv-search-hint"}
          >
            <FiSearch aria-hidden="true" />
            {loading ? t("globalSearch.searching") : t("advancedSearchForm.search")}
          </button>
          <button
            type="button"
            className="adv-search-collapse"
            aria-expanded={!collapsed}
            aria-controls="adv-search-form"
            aria-label={t(collapsed ? "advancedSearchForm.expand" : "advancedSearchForm.collapse")}
            title={t(collapsed ? "advancedSearchForm.expand" : "advancedSearchForm.collapse")}
            onClick={() => setCollapsed((value) => !value)}
          >
            {collapsed ? <FiChevronDown aria-hidden="true" /> : <FiChevronUp aria-hidden="true" />}
          </button>
        </div>
      </div>

      <form
        id="adv-search-form"
        className="adv-search-form"
        hidden={collapsed}
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        noValidate
      >
        <div className="adv-search-grid">
          {cells.map((cell) => (Array.isArray(cell) ? (
            <div key={cell.join("-")} className="adv-search-cell adv-search-pair">
              {cell.map(renderField)}
            </div>
          ) : renderField(cell)))}
        </div>
        {!canSearch && (
          <p id="adv-search-hint" className="adv-search-hint">
            {t("advancedSearchForm.hint")}
          </p>
        )}
      </form>
      {footer && <div className="adv-search-footer">{footer}</div>}
    </section>
  );
};

export default AdvancedSearchPanel;
